import {
  Avatar,
  SidePanelDS,
  Spacer,
  TextCapitalized,
  dateFormatterDayMonthLongYearAndHourMinute2Digits,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { formatHour } from "../../../../../libSlice/materialUiFormats";
import type { VehicleSession, VehicleSessionRestitution } from "../../fleetApi";
import "../../../../../i18n/i18n";

export const RestitutionDetails = (props: {
  session: VehicleSession;
  initialRestitution?: VehicleSessionRestitution;
  correctiveRestitution?: VehicleSessionRestitution;
}) => {
  const { session, initialRestitution, correctiveRestitution } = props;
  const { t } = useTranslation();

  const durationInHours = session.endOfUsage
    ? (new Date(session.endOfUsage).getTime() -
        new Date(session.startOfUsage).getTime()) /
      (1000 * 60 * 60)
    : null;

  return (
    <SidePanelDS.Section verticalSpacing={2} horizontalSpacing={1.5}>
      <SectionTitle>
        <TextCapitalized>
          {t("fleet.vehicleSessions.sidepanel.details.title")}
        </TextCapitalized>
      </SectionTitle>
      <Spacer y={2} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("fleet.vehicleSessions.sidepanel.details.submittedBy")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={
          initialRestitution ? (
            <StyledPerson>
              <Avatar
                size={"S"}
                color={"mint"}
                firstName={initialRestitution.submittedBy.firstName}
                lastName={initialRestitution.submittedBy.lastName}
              />
              <Spacer x={0.5} />
              {`${initialRestitution.submittedBy.firstName} ${initialRestitution.submittedBy.lastName}`}
            </StyledPerson>
          ) : (
            <StyledValue>-</StyledValue>
          )
        }
      />
      {correctiveRestitution && (
        <>
          <Spacer y={2} />
          <SidePanelDS.SectionRow
            label={
              <SectionRowLabel>
                <TextCapitalized>
                  {t("fleet.vehicleSessions.sidepanel.details.updatedBy")}
                </TextCapitalized>
              </SectionRowLabel>
            }
            value={
              <StyledPerson>
                <Avatar
                  size={"S"}
                  color={"mint"}
                  firstName={correctiveRestitution.submittedBy.firstName}
                  lastName={correctiveRestitution.submittedBy.lastName}
                />
                <Spacer x={0.5} />
                {`${correctiveRestitution.submittedBy.firstName} ${correctiveRestitution.submittedBy.lastName}`}
              </StyledPerson>
            }
          />
          <Spacer y={2} />
          <SidePanelDS.SectionRow
            label={
              <SectionRowLabel>
                <TextCapitalized>
                  {t("fleet.vehicleSessions.sidepanel.details.updatedAt")}
                </TextCapitalized>
              </SectionRowLabel>
            }
            value={
              <StyledValue>
                <TextCapitalized>
                  {dateFormatterDayMonthLongYearAndHourMinute2Digits(
                    new Date(correctiveRestitution.submittedAt),
                  )}
                </TextCapitalized>
              </StyledValue>
            }
          />
        </>
      )}
      <Spacer y={2} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("fleet.vehicleSessions.sidepanel.details.start")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={
          <StyledValue>
            <TextCapitalized>
              {dateFormatterDayMonthLongYearAndHourMinute2Digits(
                new Date(session.startOfUsage),
              )}
            </TextCapitalized>
          </StyledValue>
        }
      />
      <Spacer y={2} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("fleet.vehicleSessions.sidepanel.details.end")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={
          <StyledValue>
            <TextCapitalized>
              {session.endOfUsage
                ? dateFormatterDayMonthLongYearAndHourMinute2Digits(
                    new Date(session.endOfUsage),
                  )
                : "-"}
            </TextCapitalized>
          </StyledValue>
        }
      />
      <Spacer y={2} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("fleet.vehicleSessions.sidepanel.details.duration")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={
          <StyledValue>
            <TextCapitalized>
              {durationInHours !== null ? formatHour(durationInHours) : "-"}
            </TextCapitalized>
          </StyledValue>
        }
      />
      <Spacer y={2} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("fleet.vehicleSessions.sidepanel.details.departureLocation")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={
          <StyledValue>
            <TextCapitalized>
              {session.departureLocation ?? "-"}
            </TextCapitalized>
          </StyledValue>
        }
      />
      <Spacer y={2} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("fleet.vehicleSessions.sidepanel.details.furthestDestination")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={
          <StyledValue>
            <TextCapitalized>
              {session.furthestDestination ?? "-"}
            </TextCapitalized>
          </StyledValue>
        }
      />
    </SidePanelDS.Section>
  );
};

const SectionTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]}
`;

const SectionRowLabel = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/black"]}
`;

const StyledValue = styled.div`
  text-align: right;
`;

const StyledPerson = styled.div`
  ${typographies["Body/S"]}
  display: flex;
  align-items: center;
`;
